import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Input, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../common/types";
import type { UUID } from "../../../../typings/global";
import { useHasPermissions } from "../../../auth/hooks";
import ClientDrawerForm from "../../../client/components/drawers/ClientDrawerForm";
import ClientSearchInput from "../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientSearchActionType } from "../../../client/enums";
import { useClientSearch } from "../../../client/hooks";
import { Client } from "../../../client/types";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import ProductSelect from "../../../enumerations/components/form/ProductSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { ProductFinancialSector } from "../../../product/enums";
import { PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP } from "../../../product/utils";
import { Contract, CreateUpdateContract } from "../../types";
import { isContractVerified } from "../../utils";

const colSpan = 5;

interface Props {
  initialContract?: Contract;
  financialSector: ProductFinancialSector;
  privilegedChangesPermission: Permission;
  client?: Partial<Client>;
  clientViolationErrors: Map<number, FieldConstraintViolation[]>;
  onClientChange: (client?: Partial<Client>) => void;
  onClientViolationErrorsDelete: () => void;
}

export const ContractFormHeaderSection = ({
  initialContract,
  financialSector,
  privilegedChangesPermission,
  client,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const hasPrivilegedChangesPermission = useHasPermissions(privilegedChangesPermission);
  const productsEnums = useEnumeration(EnumerationType.PRODUCTS);

  const [clientFormOpen, setClientFormOpen] = useState<boolean>(false);
  const [clientProcessed, setClientProcessed] = useState<boolean>();
  const [clientStage, setClientStage] = useState<ClientFormStage | undefined>(
    initialContract?.clients[0] ? ClientFormStage.SELECTED : undefined,
  );

  const form = Form.useFormInstance<CreateUpdateContract>();
  const institutionId = Form.useWatch("institutionId", form);

  const disablePrivilegedInputs = isContractVerified(initialContract) && !hasPrivilegedChangesPermission;

  useEffect(() => {
    if (clientProcessed && clientSearch.result.keyword === form.getFieldValue(["clientIdentifiers", 0])) {
      if (clientSearch.result.data) {
        setClientStage(ClientFormStage.EXISTING);
        props.onClientChange(clientSearch.result.data);
      } else {
        setClientStage(ClientFormStage.NEW);
      }
    }
  }, [clientSearch.result]);

  const handleInstitutionIdChange = (institutionId: UUID) => {
    const productId = form.getFieldValue("productId") as UUID;

    if (!productId) {
      return;
    }

    const product = productsEnums.find(
      (product) => product.id === productId && product.institutionIds.includes(institutionId),
    );

    if (!product) {
      form.setFieldsValue({ productId: undefined });
    }
  };

  const handleClientSearchActionClick = (index: number, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setClientProcessed(true);
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        const updatedIdentifiers: string[] = [...((form.getFieldValue("clientIdentifiers") as string[]) || [])];
        updatedIdentifiers[index] = "";
        form.setFieldsValue({ clientIdentifiers: updatedIdentifiers });

        setClientStage(undefined);
        setClientProcessed(false);

        props.onClientViolationErrorsDelete();
        props.onClientChange(undefined);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string) => {
    form
      .validateFields([["clientIdentifiers", 0]])
      .then(() => {
        if (value) {
          clientSearch.onSearch({ keyword: value });
        }
      })
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string) => {
    if (clientStage) {
      setClientStage(undefined);
    }

    if (client) {
      props.onClientChange(undefined);
    }

    if (props.clientViolationErrors) {
      props.onClientViolationErrorsDelete();
    }

    handleClientSearchSubmit(value);
  };

  const handleClientFormSubmit = (client: Client) => {
    setClientFormOpen(false);
    setClientProcessed(false);
    setClientStage(ClientFormStage.SELECTED);
    props.onClientViolationErrorsDelete();
    props.onClientChange(client);
  };

  const getClientFormIdentifier = (): string | undefined => {
    return ((form.getFieldValue("clientIdentifiers") as string[]) || [])[0];
  };

  return (
    <>
      <Card type="inner" className="card-box margin-top-medium" title={t("contract.sections.contractIdentification")}>
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="contractNumber"
              label={t("contract.attrs.contractNumber")}
              dependencies={["secondaryContractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("secondaryContractNumber", t("contract.attrs.otherContractNumber")),
                validations.notEqualTo("secondaryContractNumber", t("contract.attrs.otherContractNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryContractNumber"
              label={t("contract.attrs.otherContractNumber")}
              dependencies={["contractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("contractNumber", t("contract.attrs.contractNumber")),
                validations.notEqualTo("contractNumber", t("contract.attrs.contractNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("common.institution"),
                rules: [validations.notNull],
              }}
              selectProps={{ disabled: disablePrivilegedInputs, onChange: handleInstitutionIdChange }}
              optionsProps={{
                selected: initialContract ? [initialContract.institution] : undefined,
                filterType: PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP.get(financialSector),
              }}
            />
          </Col>

          <Col span={colSpan}>
            <ProductSelect
              formItemProps={{
                name: "productId",
                label: t("contract.attrs.productId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                institutionId,
                selectedInTree: {
                  selectedProductId: initialContract?.product.id,
                  condition: initialContract?.institution.id === institutionId,
                },
                hideAll: !institutionId,
                groupByProductGroup: true,
                filterSectors: [financialSector],
              }}
              selectProps={{
                disabled: disablePrivilegedInputs,
                placeholder: !institutionId ? t("contract.helpers.commonProductPlaceholder") : undefined,
              }}
            />
          </Col>
        </Row>

        <Divider titlePlacement="start">{t("contract.sections.clients")}</Divider>

        <Row gutter={rowGutter}>
          <ClientSearchInput
            formItemProps={{
              name: ["clientIdentifiers", 0],
              label: (
                <LabelWithTooltip tooltip={t("helpers.pinFormat")} label={t("contract.attrs.policyHolderIdentifier")} />
              ),
              rules: [validations.notBlank, validations.pinOrCrn],
            }}
            formStage={clientStage}
            formType={0}
            processedType={0}
            client={client}
            violationErrors={props.clientViolationErrors}
            inProgress={clientSearch.inProgress}
            inputColSpan={5}
            clientNameColSpan={6}
            onActionClick={handleClientSearchActionClick}
            onFocus={() => setClientProcessed(true)}
            onSearch={handleClientSearchSubmit}
            onChange={handleClientSearchChange}
          />
        </Row>
      </Card>

      <ClientDrawerForm<number>
        open={clientFormOpen}
        client={client}
        initialIdentifier={getClientFormIdentifier()}
        formType={0}
        violationErrors={props.clientViolationErrors}
        onFormSubmit={handleClientFormSubmit}
      />
    </>
  );
};
